import { AfterViewInit, Component, ElementRef, forwardRef, HostBinding, HostListener, Input, ViewChild, ViewEncapsulation } from '@angular/core';
import { AbstractControl, ControlValueAccessor, NG_VALIDATORS, NG_VALUE_ACCESSOR, ValidationErrors, Validator } from '@angular/forms';

@Component({
    selector: 'jhi-formula-editor',
    templateUrl: './formula-editor.component.html',
    styleUrls: ['./formula-editor.component.scss'],
    encapsulation: ViewEncapsulation.None,
    providers: [
        {
            provide: NG_VALUE_ACCESSOR,
            useExisting: forwardRef(() => FormulaEditorComponent),
            multi: true
        },
        {
            provide: NG_VALIDATORS,
            useExisting: forwardRef(() => FormulaEditorComponent),
            multi: true
        }
    ]
})
export class FormulaEditorComponent implements ControlValueAccessor, Validator, AfterViewInit {
    @ViewChild('editable', { static: true }) editableRef!: ElementRef<HTMLDivElement>;

    @HostBinding('class.required-field')
    get isRequiredField(): boolean {
        return this.required === true;
    }

    @HostListener('document:selectionchange')
    onSelectionChange(): void {
        this.captureSelection();
    }

    private _maxLength = 255;
    private _required = false;
    private _disabled = false;

    private lastRange: Range | null = null;
    private value = '';
    private validatorChangeFn: () => void = () => {
    };
    private onChange: (value: string) => void = () => {
    };
    private onTouched: () => void = () => {
    };


    private captureSelection(): void {
        const selection = window.getSelection();
        const editor = this.editableRef?.nativeElement;

        if (!selection || !editor || selection.rangeCount === 0) {
            return;
        }

        const range = selection.getRangeAt(0);
        const container = range.commonAncestorContainer;
        const insideEditor = editor.contains(
            container.nodeType === Node.ELEMENT_NODE ? container : container.parentNode
        );

        if (insideEditor) {
            this.lastRange = range.cloneRange();
        }
    }

    private placeCaretAtEnd(): void {
        const editor = this.editableRef?.nativeElement;
        if (!editor) {
            return;
        }

        const range = document.createRange();
        range.selectNodeContents(editor);
        range.collapse(false);

        const selection = window.getSelection();
        if (!selection) {
            return;
        }

        selection.removeAllRanges();
        selection.addRange(range);
        this.lastRange = range.cloneRange();
    }

    @Input()
    set maxLength(value: number) {
        this._maxLength = Number(value ?? 0);
        this.validatorChangeFn();
    }

    get maxLength(): number {
        return this._maxLength;
    }

    @Input()
    set required(value: boolean) {
        this._required = value === true || String(value) === 'true';
        this.validatorChangeFn();
    }

    get required(): boolean {
        return this._required;
    }

    @Input()
    set disabled(value: boolean) {
        this._disabled = value === true || String(value) === 'true';
        this.updateEditableState();
    }

    get disabled(): boolean {
        return this._disabled;
    }

    ngAfterViewInit(): void {
        this.renderFromDefinition(this.value);
        this.updateEditableState();
    }

    writeValue(value: string | null): void {
        this.value = value ?? '';
        this.renderFromDefinition(this.value);
        this.lastRange = null;
        this.updateEditableState();
        this.validatorChangeFn();
    }

    registerOnChange(fn: (value: string) => void): void {
        this.onChange = fn;
    }

    registerOnTouched(fn: () => void): void {
        this.onTouched = fn;
    }

    setDisabledState(isDisabled: boolean): void {
        this._disabled = isDisabled;
        this.updateEditableState();
    }

    validate(control: AbstractControl): ValidationErrors | null {
        const currentValue = (control.value ?? '').toString();

        if (this.required && currentValue.trim().length === 0) {
            return { required: true };
        }

        if (this.maxLength > 0 && currentValue.length > this.maxLength) {
            return {
                maxlength: {
                    requiredLength: this.maxLength,
                    actualLength: currentValue.length
                }
            };
        }

        return null;
    }

    registerOnValidatorChange(fn: () => void): void {
        this.validatorChangeFn = fn;
    }

    public insertToken(token: string): void {
        if (this.disabled) {
            return;
        }

        const normalizedToken = (token ?? '').trim();
        if (!normalizedToken) {
            return;
        }

        const current = this.extractDefinitionFromDom();
        const tokenAsDefinition = `{{${normalizedToken}}}`;

        if (this.maxLength > 0 && current.length + tokenAsDefinition.length + 1 > this.maxLength) {
            return;
        }

        this.focus();

        const span = document.createElement('span');
        span.className = 'token';
        span.contentEditable = 'false';
        span.dataset['token'] = normalizedToken;
        span.textContent = `{{${normalizedToken}}}`;

        this.insertNodeAtCaret(span);
        this.insertNodeAtCaret(document.createTextNode(' '));
        this.syncToModel();
        this.onTouched();
    }

    public focus(): void {
        if (this.disabled) {
            return;
        }

        this.editableRef?.nativeElement?.focus();

        const selection = window.getSelection();
        if (!selection) {
            return;
        }

        selection.removeAllRanges();

        if (this.lastRange) {
            selection.addRange(this.lastRange.cloneRange());
        } else {
            this.placeCaretAtEnd();
        }
    }

    onBlur(): void {
        this.captureSelection();
        this.onTouched();
        this.syncToModel();
        this.validatorChangeFn();
    }

    onKeyDown(event: KeyboardEvent): void {
        if (this.disabled) {
            event.preventDefault();
            return;
        }

        if (event.ctrlKey || event.metaKey) {
            return;
        }

        if (event.key === 'Enter') {
            event.preventDefault();
            return;
        }

        const allowedKeys = [
            'Backspace',
            'Delete',
            'ArrowLeft',
            'ArrowRight',
            'ArrowUp',
            'ArrowDown',
            'Home',
            'End',
            'Tab'
        ];

        if (allowedKeys.includes(event.key)) {
            return;
        }

        const current = this.extractDefinitionFromDom();
        if (this.maxLength > 0 && current.length >= this.maxLength) {
            event.preventDefault();
        }
    }

    onPaste(event: ClipboardEvent): void {
        if (this.disabled) {
            event.preventDefault();
            return;
        }

        event.preventDefault();

        const pastedText = event.clipboardData?.getData('text/plain') ?? '';
        const current = this.extractDefinitionFromDom();
        const remaining = Math.max(0, this.maxLength - current.length);
        const textToInsert = this.maxLength > 0 ? pastedText.slice(0, remaining) : pastedText;

        this.insertNodeAtCaret(document.createTextNode(textToInsert));
        this.syncToModel();
        this.onTouched();
    }

    private updateEditableState(): void {
        if (this.editableRef?.nativeElement) {
            this.editableRef.nativeElement.contentEditable = (!this.disabled).toString();
        }
    }

    private syncToModel(): void {
        const definition = this.extractDefinitionFromDom();
        this.value = definition;
        this.onChange(definition);
        this.validatorChangeFn();
    }

    private renderFromDefinition(definition: string): void {
        if (!this.editableRef?.nativeElement) {
            return;
        }

        const editableElement = this.editableRef.nativeElement;
        editableElement.innerHTML = '';

        const regex = /\{\{([\w\r\s_]+)\}\}/g;
        let lastIndex = 0;
        let match: RegExpExecArray | null;

        while ((match = regex.exec(definition)) !== null) {
            const before = definition.slice(lastIndex, match.index);
            if (before) {
                editableElement.appendChild(document.createTextNode(before));
            }

            const tokenText = (match[1] ?? '').trim();
            const span = document.createElement('span');
            span.className = 'token';
            span.contentEditable = 'false';
            span.dataset['token'] = tokenText;
            span.textContent = `{{${tokenText}}}`;
            editableElement.appendChild(span);

            lastIndex = match.index + match[0].length;
        }

        const tail = definition.slice(lastIndex);
        if (tail) {
            editableElement.appendChild(document.createTextNode(tail));
        }
    }

    private extractDefinitionFromDom(): string {
        if (!this.editableRef?.nativeElement) {
            return '';
        }

        const editableElement = this.editableRef.nativeElement;
        let result = '';

        editableElement.childNodes.forEach(node => {
            if (node.nodeType === Node.TEXT_NODE) {
                result += node.textContent ?? '';
            } else if (node.nodeType === Node.ELEMENT_NODE) {
                const element = node as HTMLElement;

                if (element.classList.contains('token')) {
                    const token = (element.dataset['token'] ?? '').trim();
                    result += `{{${token}}}`;
                } else {
                    result += element.textContent ?? '';
                }
            }
        });

        return result;
    }

    private insertNodeAtCaret(node: Node): void {
        const selection = window.getSelection();
        const editor = this.editableRef.nativeElement;

        if (!selection) {
            editor.appendChild(node);
            this.placeCaretAtEnd();
            return;
        }

        let range: Range;

        if (selection.rangeCount > 0) {
            range = selection.getRangeAt(0);
            const container = range.commonAncestorContainer;
            const insideEditor = editor.contains(
                container.nodeType === Node.ELEMENT_NODE ? container : container.parentNode
            );

            if (!insideEditor) {
                this.placeCaretAtEnd();
                if (selection.rangeCount === 0) {
                    editor.appendChild(node);
                    this.placeCaretAtEnd();
                    return;
                }
                range = selection.getRangeAt(0);
            }
        } else {
            this.placeCaretAtEnd();
            if (selection.rangeCount === 0) {
                editor.appendChild(node);
                this.placeCaretAtEnd();
                return;
            }
            range = selection.getRangeAt(0);
        }

        range.deleteContents();
        range.insertNode(node);
        range.setStartAfter(node);
        range.setEndAfter(node);

        selection.removeAllRanges();
        selection.addRange(range);
        this.lastRange = range.cloneRange();
    }
}
